// Provably-fair "Дом Псов" — механика в стиле The Dog House
// (5×3, 20 линий, wild с множителем на средних барабанах, sticky-wild фриспины).
// Правила игры не охраняются копирайтом; ассеты/название/код — оригинальные.
import { hmacSha256Hex } from './index';
export const DH_COLS = 5;
export const DH_ROWS = 3;
export const DH_LINES = 20;
export const DH_FREE_SPINS = 10;
export const DH_RETRIGGER = 5;
export const DH_MAX_SPINS = 60;
export const DH_SCATTER_TRIGGER = 3;
export const DH_RTP_SCALE = 0.76; // калибровано симуляцией под ~0.96
// покупка бонуса: цена = ×ставки, scale так, чтобы RTP покупки ~0.96
export const DH_BONUS_BUY_COST = 100;
export const DH_BONUS_BUY_SCALE = 2.0672; // покупка ~0.96 RTP при цене ×100
const NONWILD = ['scatter', 'h1', 'h2', 'h3', 'h4', 'l1', 'l2', 'l3', 'l4'];
const NONWILD_W = [4, 6, 8, 9, 10, 14, 15, 16, 18];
const WILD_W = 8; // вес wild на средних барабанах (0,4 — без wild)
const MULT_VALUES = [2, 3];
const MULT_W = [3, 2];
const MIDDLE = new Set([1, 2, 3]); // барабаны с wild
export const DH_PAYTABLE = {
    wild: [10, 50, 200],
    h1: [5, 20, 80],
    h2: [2.5, 10, 40],
    h3: [2, 6, 25],
    h4: [1.5, 5, 20],
    l1: [0.5, 2, 8],
    l2: [0.4, 1.5, 6],
    l3: [0.3, 1, 5],
    l4: [0.25, 0.8, 4],
};
export const DH_PAYLINES = [
    [1, 1, 1, 1, 1], [0, 0, 0, 0, 0], [2, 2, 2, 2, 2], [0, 1, 2, 1, 0], [2, 1, 0, 1, 2],
    [0, 0, 1, 0, 0], [2, 2, 1, 2, 2], [1, 2, 1, 0, 1], [1, 0, 1, 2, 1], [0, 1, 1, 1, 0],
    [2, 1, 1, 1, 2], [1, 1, 0, 1, 1], [1, 1, 2, 1, 1], [0, 1, 0, 1, 0], [2, 1, 2, 1, 2],
    [1, 0, 0, 0, 1], [1, 2, 2, 2, 1], [0, 0, 2, 0, 0], [2, 2, 0, 2, 2], [0, 2, 0, 2, 0],
];
function makeRng(serverSeed, clientSeed, nonce) {
    let counter = 0, buf = '', pos = 0;
    function hex(n) {
        while (buf.length - pos < n)
            buf += hmacSha256Hex(serverSeed, `${clientSeed}:${nonce}:${counter++}`);
        const s = buf.slice(pos, pos + n);
        pos += n;
        return s;
    }
    const unit = () => parseInt(hex(8), 16) / 0x100000000;
    function pick(items, weights) {
        const tot = weights.reduce((a, b) => a + b, 0);
        let r = unit() * tot;
        for (let i = 0; i < items.length; i++) {
            if (r < weights[i])
                return items[i];
            r -= weights[i];
        }
        return items[items.length - 1];
    }
    return { pick, unit };
}
function pickCell(rng, reel) {
    if (MIDDLE.has(reel) && rng.unit() * (NONWILD_W.reduce((a, b) => a + b, 0) + WILD_W) >= NONWILD_W.reduce((a, b) => a + b, 0)) {
        return { s: 'wild', m: rng.pick(MULT_VALUES, MULT_W) };
    }
    return { s: rng.pick(NONWILD, NONWILD_W) };
}
function evalGrid(grid, lineBet) {
    const lineWins = [];
    DH_PAYLINES.forEach((rows, li) => {
        const cells = rows.map((row, reel) => grid[reel][row]);
        let target = null;
        for (const c of cells)
            if (c.s !== 'wild' && c.s !== 'scatter') {
                target = c.s;
                break;
            }
        if (target === null)
            target = 'wild';
        let count = 0, mult = 1;
        for (const c of cells) {
            if (c.s === target || c.s === 'wild') {
                if (c.s === 'wild' && c.m)
                    mult *= c.m;
                count++;
            }
            else
                break;
        }
        if (count >= 3) {
            const pay = DH_PAYTABLE[target][count - 3] || 0;
            if (pay > 0)
                lineWins.push({ line: li, sym: target, count, mult, win: pay * lineBet * mult });
        }
    });
    let scatterCount = 0;
    for (const col of grid)
        for (const c of col)
            if (c.s === 'scatter')
                scatterCount++;
    const spinWin = lineWins.reduce((s, w) => s + w.win, 0);
    return { grid, lineWins, scatterCount, spinWin };
}
function baseGrid(rng) {
    return Array.from({ length: DH_COLS }, (_, reel) => Array.from({ length: DH_ROWS }, () => pickCell(rng, reel)));
}
export function playDogHouse(serverSeed, clientSeed, nonce, betAmount, bonusBuy = false) {
    const rng = makeRng(serverSeed, clientSeed, nonce);
    const lineBet = betAmount / DH_LINES;
    const base = evalGrid(baseGrid(rng), lineBet);
    if (bonusBuy) {
        // косметика: показываем скаттеры для анимации «BONUS!»
        for (let r = 0; r < DH_SCATTER_TRIGGER; r++)
            base.grid[r][1] = { s: 'scatter' };
    }
    const free = [];
    let freeSpinsAwarded = 0;
    if (bonusBuy || base.scatterCount >= DH_SCATTER_TRIGGER) {
        const sticky = new Map(); // "reel-row" -> mult
        let spins = DH_FREE_SPINS;
        let i = 0;
        while (i < spins && i < DH_MAX_SPINS) {
            const grid = Array.from({ length: DH_COLS }, () => []);
            for (let reel = 0; reel < DH_COLS; reel++) {
                for (let row = 0; row < DH_ROWS; row++) {
                    const key = `${reel}-${row}`;
                    if (sticky.has(key)) {
                        grid[reel][row] = { s: 'wild', m: sticky.get(key), sticky: true };
                    }
                    else {
                        const cell = pickCell(rng, reel);
                        if (cell.s === 'wild') {
                            sticky.set(key, cell.m || 2);
                            grid[reel][row] = { ...cell, sticky: true };
                        }
                        else {
                            grid[reel][row] = cell;
                        }
                    }
                }
            }
            const spin = evalGrid(grid, lineBet);
            free.push(spin);
            if (spin.scatterCount >= DH_SCATTER_TRIGGER)
                spins += DH_RETRIGGER;
            i++;
        }
        freeSpinsAwarded = i;
    }
    // spinWin уже в деньгах (× lineBet). При покупке база не платит. Применяем scale.
    const baseWin = bonusBuy ? 0 : base.spinWin;
    const scale = DH_RTP_SCALE * (bonusBuy ? DH_BONUS_BUY_SCALE : 1);
    const total = (baseWin + free.reduce((s, f) => s + f.spinWin, 0)) * scale;
    return { base, free, freeSpinsAwarded, totalWin: total };
}
//# sourceMappingURL=doghouse.js.map